import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { collection, getDocs, orderBy, arrayUnion, arrayRemove, query, doc, setDoc, updateDoc, increment, getDoc, onSnapshot } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";


function HomePage() {
  const [expandedPost, setExpandedPost] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [expandedRatings, setExpandedRatings] = useState({});
  const [likedPosts, setLikedPosts] = useState({});
  const [mostViewed, setMostViewed] = useState([]);
  const [postLimit, setPostLimit] = useState(40);
  const [followingUsers, setFollowingUsers] = useState([]);

  // ✅ Search filters
  const [companySearch, setCompanySearch] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [positionSearch, setPositionSearch] = useState("");
  const [userIdSearch, setUserIdSearch] = useState("");

// ✅ Change layout for mobile
  const [selectedView, setSelectedView] = useState("latest");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);
  
  // ✅ Manages visibility of reply input fields for each post
const [replyVisibility, setReplyVisibility] = useState({});

// ✅ Stores the text entered in the reply input fields for each post
const [replyText, setReplyText] = useState({});

// ✅ Toggle the reply input field visibility
const toggleReplyBox = (postId, source, commentId = null) => {
  const visibilityKey = `${postId}-${source}-${commentId || "main"}`;
  const textKey = commentId || `${postId}-main`;



  setReplyVisibility(prev => ({
    ...prev,
    [visibilityKey]: prev[visibilityKey] ? null : "comment"  // Toggle open/close
  }));

  setReplyText(prev => ({
    ...prev,
    [textKey]: prev[textKey] || ""
  }));
};


	const ratingLabels = {
  management: "Management",
  coworkers: "Coworkers",
  supportStaff: "Support Staff",
  compensation: "Compensation",
  benefits: "Benefits",
  hr: "HR",
  overall: "Overall",
};


//✅ Toggle expanded state per post:
const toggleExpandedComments = (postId) => {
  setExpandedComments(prev => ({
    ...prev,
    [postId]: !prev[postId]
  }));
};


	// ✅ T. Score Formula
	const calculateTrendingScore = (post) => {
  const now = Date.now();

  const createdAt = post.createdAt?.toDate
    ? post.createdAt.toDate().getTime()
    : new Date(post.createdAt).getTime();

  if (!createdAt || isNaN(createdAt)) return 0;

  const age = (now - createdAt) / (1000 * 60 * 60); // Age in hours

  const repliesCount = post.responses?.reduce((acc, r) => acc + (r.replies?.length || 0), 0) || 0;
  const commentsCount = post.responses?.length || 0;
  const engagementScore = (post.views || 0) + (post.likes || 0) * 2 + (commentsCount + repliesCount) * 3;

  if (age < 24) {
    // 💡 Grace period: full score for the first 24 hours
    return engagementScore;
  }

  // ⏳ After 24h, apply time decay
  const timeWeighted = engagementScore / Math.pow(age + 2, 1.1);

  return Math.round(timeWeighted);
};


// ✅ Handle Reply Submission
const handleSubmitReply = async (postId, parentCommentId = null, source = "latest") => {
  if (!auth.currentUser) {
    console.error("User not authenticated");
    return;
  }

  const postRef = doc(db, "reviews", postId);
  const postSnap = await getDoc(postRef);
  if (!postSnap.exists()) return;

  let existingResponses = postSnap.data().responses || [];

  // ✅ Get User's Username
  const userRef = doc(db, "users", auth.currentUser.uid);
  const userSnap = await getDoc(userRef);
  const username = userSnap.exists() ? userSnap.data().username : "Anonymous";

  const inputKey = parentCommentId
  ? parentCommentId
  : `${postId}-${source}-main`;


  const replyContent = replyText[inputKey]?.trim();
  if (!replyContent) return; // Don't submit empty text

  const newReply = {
    id: Date.now().toString(),
    username,
    text: replyContent,
    createdAt: new Date().toISOString(),
    replies: []
  };

  let updatedResponses;
  if (parentCommentId) {
    updatedResponses = existingResponses.map(comment =>
      comment.id === parentCommentId
        ? { ...comment, replies: [...(comment.replies || []), newReply] }
        : comment
    );
  } else {
    updatedResponses = [...existingResponses, newReply];
  }

  try {
    // ✅ Update UI immediately
    setPosts(prevPosts =>
      prevPosts.map(post =>
        post.id === postId ? { ...post, responses: updatedResponses } : post
      )
    );

    // ✅ Clear input field
    setReplyText(prev => ({
      ...prev,
      [inputKey]: ""
    }));

    // ✅ Close reply/comment box
   setReplyVisibility(prev => {
  const key = parentCommentId ? parentCommentId : `${postId}-${source}-main`;
  return { ...prev, [key]: null };
});



    // ✅ Update Firestore
    await setDoc(postRef, { responses: updatedResponses }, { merge: true });

  } catch (error) {
    console.error("❌ Error saving reply:", error);
  }
};


  useEffect(() => {
  const handleResize = () => {
    setIsPortrait(window.innerHeight > window.innerWidth);
  };
  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);


 useEffect(() => {
  const fetchFollowingUsers = async () => {
    if (!auth.currentUser) return;

    try {
      const userRef = doc(db, "users", auth.currentUser.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const followingList = userSnap.data().following || [];
        setFollowingUsers(followingList);
      }
    } catch (error) {
      console.error("Error fetching following users:", error);
    }
  };

  fetchFollowingUsers();
}, [currentUser]); // Ensure it updates when the user logs in



const toggleLike = async (postId) => {
  const postRef = doc(db, "reviews", postId);
  const postSnap = await getDoc(postRef);
  if (!postSnap.exists()) return;

  let postData = postSnap.data();
  const localLiked = JSON.parse(localStorage.getItem("likedPosts") || "{}");
  const hasLiked = localLiked[postId];

  const currentLikes = typeof postData.likes === "number" ? postData.likes : 0;
  const updatedLikes = hasLiked ? currentLikes - 1 : currentLikes + 1;

  try {
    await updateDoc(postRef, { likes: updatedLikes });
    console.log(`✅ Updated post ${postId}: likes = ${updatedLikes}`);

    localLiked[postId] = !hasLiked;
    localStorage.setItem("likedPosts", JSON.stringify(localLiked));

    setLikedPosts(prev => ({ ...prev, [postId]: !hasLiked }));
    setPosts(prevPosts =>
      prevPosts.map(post =>
        post.id === postId ? { ...post, likes: updatedLikes } : post
      )
    );
  } catch (err) {
    console.error("❌ Failed to update likes:", err);
  }
};


// ✅ resize for mobile
useEffect(() => {
  const handleResize = () => {
    setIsMobile(window.innerWidth < 768);
  };
  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);



// ✅ Fetch Liked Posts from Firestore on Page Load
useEffect(() => {
  async function fetchLikedPosts() {
    if (!currentUser) return;
    const querySnapshot = await getDocs(collection(db, "reviews"));
    
    let likedData = {};
    querySnapshot.forEach(doc => {
      const post = doc.data();
      if (post.likedBy?.includes(currentUser.uid)) {
        likedData[doc.id] = true;
      }
    });

    setLikedPosts(likedData);
  }

  fetchLikedPosts();
}, [currentUser]);


const handleFollowToggle = async (userId, username) => {
  if (!auth.currentUser) {
    console.error("User not logged in.");
    return;
  }

  const currentUserRef = doc(db, "users", auth.currentUser.uid);
  const currentUserSnap = await getDoc(currentUserRef);

  if (!currentUserSnap.exists()) {
    console.error("Current user document not found.");
    return;
  }

  let following = currentUserSnap.data().following || [];

  // Check if the user is already being followed
  const isAlreadyFollowing = following.some(f => f.userId === userId);

  if (isAlreadyFollowing) {
    // ✅ Remove from the following list
    following = following.filter(f => f.userId !== userId);
  } else {
    // ✅ Ensure username is stored correctly
    try {
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);
      const actualUsername = userSnap.exists() ? userSnap.data().username : username; 

      following.push({ userId, username: actualUsername });

    } catch (error) {
      console.error("Error fetching username:", error);
    }
  }

  // ✅ Update Firestore and React State
  await updateDoc(currentUserRef, { following });

  // ✅ Update local state so button updates instantly
  setFollowingUsers([...following]);  
}; 



  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const q = query(collection(db, "reviews"), orderBy("createdAt", "desc"));
        const querySnapshot = await getDocs(q);

        let postsList = [];

        for (let docSnap of querySnapshot.docs) {
          let postData = docSnap.data();
          let userInfo = {};

          // Use post's own location fields if it's a question
          if (postData.isQuestion) {
            userInfo = {
              company: postData.company || "N/A",
              city: postData.city || "N/A",
              state: postData.state || "N/A",
              department: postData.department || "N/A"
            };
          } else {
            // Else, get from user profile
            if (postData.userId) {
              const userRef = doc(db, "users", postData.userId);
              const userSnap = await getDoc(userRef);
              if (userSnap.exists()) {
                userInfo = {
                  company: userSnap.data().company || "N/A",
                  position: userSnap.data().position || "N/A",
                  city: postData.city || "N/A",
                  state: postData.state || "N/A"
                };
              }
            }
          }


        postsList.push({
          id: docSnap.id,
          ...postData,
          ...userInfo, // ✅ Attach fetched user data
          responses: postData.responses || [],
        });
      }

      setPosts(postsList);
    } catch (error) {
      console.error("❌ Error fetching posts:", error);
    }
  };

  fetchPosts();
}, []);



useEffect(() => {
  const q = query(collection(db, "reviews"), orderBy("createdAt", "desc"));

  const unsubscribe = onSnapshot(q, (snapshot) => {
    setPosts(snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      responses: doc.data().responses || [] // ✅ Ensure responses always exist
    })));
  });

  return () => unsubscribe(); // ✅ Cleanup listener on unmount
}, []);


	// ✅ Calculate Most Viewed with engagement formula

	useEffect(() => {
  const scoredPosts = posts.map(post => ({
    ...post,
    tscore: calculateTrendingScore(post)
  }));

  const sortedPosts = [...scoredPosts].sort((a, b) => b.tscore - a.tscore);

  console.log("Trending post order:");
  sortedPosts.forEach(p =>
    console.log(`Post ${p.id} → Tscore: ${p.tscore}, Views: ${p.views}, Likes: ${p.likes}, Time: ${p.createdAt?.toDate?.() || p.createdAt}`)
  );

  setMostViewed(sortedPosts.slice(0, 50));
}, [posts]);


  // ✅ Filters apply to all posts
  const filteredPosts = posts.filter(post =>
    (!companySearch || post.company?.toLowerCase().includes(companySearch.toLowerCase())) &&
    (!citySearch || post.city?.toLowerCase().includes(citySearch.toLowerCase())) &&
    (!stateSearch || post.state?.toLowerCase().includes(stateSearch.toLowerCase())) &&
    (!positionSearch || post.position?.toLowerCase().includes(positionSearch.toLowerCase())) &&
    (!userIdSearch || post.username?.toLowerCase().includes(userIdSearch.toLowerCase()))
  );

  // ✅ Function to toggle expanded view
 const toggleReadMore = (post) => {
  if (expandedPost === post.id) {
    setExpandedPost(null);
  } else {
    setExpandedPost(post.id);

    const viewedKey = `viewed_${post.id}`;
    const alreadyViewed = localStorage.getItem(viewedKey);

    if (!alreadyViewed) {
      // ✅ Update Firestore view count
      const postRef = doc(db, "reviews", post.id);
      updateDoc(postRef, {
        views: increment(1)
      });

      // ✅ Mark as viewed locally
      localStorage.setItem(viewedKey, "true");

      // ✅ Update local post state
      setPosts((prevPosts) =>
        prevPosts.map((p) =>
          p.id === post.id
            ? { ...p, views: (p.views || 0) + 1 }
            : p
        )
      );
    }
  }
};


  // ✅ Ensure `increaseViewCount` is defined
  const increaseViewCount = async (post) => {
  const postRef = doc(db, "reviews", post.id);
  const updatedData = {
    views: (post.views || 0) + 1,
  };

  console.log("📤 Attempting to send this update:", updatedData);

  try {
    await updateDoc(postRef, updatedData);
    console.log("✅ Views updated in Firestore.");
  } catch (error) {
    console.error("❌ Error updating views:", error);
  }
};

  return (
  <div style={{ overflowX: "hidden", maxWidth: "100vw" }}>
 {/* ✅ Main container */}

	
 {/* ✅ LOGO */}
 <div style={{
  position: "relative",
  padding: "0px",
  backgroundColor: "#f9f9f9",
  overflow: "hidden",
  minHeight: "300px"
}}>
  {/* ✅ Background Logo (only on landscape or desktop) */}
  {!isPortrait && (
    <img
      src="/vibescan-logo.png"
      alt="VibeScan Logo"
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-1%, -50%)",
        opacity: 0.45,
        width: "80%",
        maxWidth: "500px",
        zIndex: 0,
        pointerEvents: "none"
      }}
    />
   )}


  {/* ✅ Foreground Text */}
  <div style={{ position: "relative", zIndex: 1, maxWidth: "700px" }}>
    <h1 style={{ fontSize: "32px", fontWeight: "700", marginBottom: "15px" }}>
      Welcome to <span style={{ color: "#0077cc", fontStyle: "italic" }}>VibeScan</span>
    </h1>
    <ul style={{ listStyle: "none", padding: 0 }}>
      <li style={{ marginBottom: "10px" }}>✅ Share what it’s really like working at your company?</li>
      <li style={{ marginBottom: "10px" }}>✅ Have a concern that is being ignored? Call them out!</li>
      <li style={{ marginBottom: "10px" }}>✅ Wondering whether to accept a new job offer? Ask an insider anonymously before you decide.</li>
      <li style={{ marginBottom: "10px" }}>✅ Communicate with coworkers, clients, supervisors, or outsiders without risking your position.</li>
      <li style={{ marginBottom: "0px" }}>✅ Wander what your employees realy think about you and the company? Ask, and let them answer anonymously.</li>
    </ul>
  </div>
</div>


    {/* ✅ Search Filters */}
    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "15px" }}>
      <input 
  type="text" 
  placeholder="Search by Company" 
  value={companySearch} 
  onChange={(e) => setCompanySearch(e.target.value)}
  style={{ 
    border: "none", 
    borderBottom: "2px solid #ccc", 
    outline: "none", 
    padding: "8px", 
    marginBottom: "10px" 
  }}
/>


<input 
  type="text" 
  placeholder="Search by City" 
  value={citySearch} 
  onChange={(e) => setCitySearch(e.target.value)}
  style={{ 
    border: "none", 
    borderBottom: "2px solid #ccc", 
    outline: "none", 
    padding: "8px", 
    marginBottom: "10px" 
  }}
/>



<input 
  type="text" 
  placeholder="Search by State" 
  value={stateSearch} 
  onChange={(e) => setStateSearch(e.target.value)}
  style={{ 
    border: "none", 
    borderBottom: "2px solid #ccc", 
    outline: "none", 
    padding: "8px", 
    marginBottom: "10px" 
  }}
/>


<input 
  type="text" 
  placeholder="Search by Position" 
  value={positionSearch} 
  onChange={(e) => setPositionSearch(e.target.value)}
  style={{ 
    border: "none", 
    borderBottom: "2px solid #ccc", 
    outline: "none", 
    padding: "8px", 
    marginBottom: "10px" 
  }}
/>

      
 
<input 
  type="text" 
  placeholder="Search by Username" 
  value={userIdSearch} 
  onChange={(e) => setUserIdSearch(e.target.value)}
  style={{ 
    border: "none", 
    borderBottom: "2px solid #ccc", 
    outline: "none", 
    padding: "8px", 
    marginBottom: "10px" 
  }}
/>
      
    </div>

    {/* ✅ Post Limit Selector */}
    <label> Show: </label>
    <select onChange={(e) => setPostLimit(Number(e.target.value))}>
      <option value="40">Last 40</option>
      <option value="80">Last 80</option>
      <option value="160">Last 160</option>
      <option value="320">Last 320</option>	
    </select>


  {/* ✅ Show toggle buttons on mobile only */}
{isMobile && (
  <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginBottom: "10px" }}>
    <button
      onClick={() => setSelectedView("latest")}
      style={{
        padding: "8px 16px",
        borderRadius: "5px",
        border: "1px solid #ccc",
        backgroundColor: selectedView === "latest" ? "#007BFF" : "#f0f0f0",
        color: selectedView === "latest" ? "white" : "black",
        cursor: "pointer"
      }}
    >
      Latest
    </button>
    <button
      onClick={() => setSelectedView("trending")}
      style={{
        padding: "8px 16px",
        borderRadius: "5px",
        border: "1px solid #ccc",
        backgroundColor: selectedView === "trending" ? "#007BFF" : "#f0f0f0",
        color: selectedView === "trending" ? "white" : "black",
        cursor: "pointer"
      }}
    >
      Trending
    </button>
  </div>
)}


    {/* ✅ Two-Column Layout */}
    <div style={{ display: isMobile ? "block" : "flex", gap: "2px" }}>
      
   

      {/* ✅ Left Column: Latest Posts */}
     {(!isMobile || selectedView === "latest") && (
     <div
    style={{
      flex: "1.2",
      minWidth: "350px",
      maxWidth: "610px",
      backgroundColor: "#f0f8ff",
      padding: "10px",
      borderRadius: "8px",
      boxShadow: "0 0 6px rgba(0,0,0,0.05)"
    }}
  >
    <h3 style={{
      textAlign: "center",
      fontSize: "24px",
      marginBottom: "5px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      justifyContent: "center"
    }}>
       Latest
    </h3>

  {filteredPosts.length === 0 ? <p>No posts available.</p> : (
    <ul style={{ listStyleType: "none", padding: 0 }}>
      {filteredPosts.slice(0, postLimit).map(post => (
        <li
  key={post.id}
  style={{
    marginBottom: "10px",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    padding: "10px",
    backgroundColor: "#fff",
    position: "relative",
  }}
>
 
          <h3>
            {post.isQuestion ? (
              <>
                <strong>Question for: </strong>
                {post.company} ({post.city}, {post.state})<br />
                <strong>By: </strong>
                {post.username || "Anonymous"}
              </>
            ) : (
              <>
                <Link to={`/user/${post.userId}`} style={{ textDecoration: "none", color: "black" }}>
                  {post.company} ({post.city}, {post.state})
                </Link>{" "}
                {post.ratings?.overall ? (
                  <span
                    onClick={() => setExpandedRatings(prev => ({ ...prev, [post.id]: !prev[post.id] }))}
                    style={{ cursor: "pointer", color: "#f5c518", fontSize: "16px", marginLeft: "5px" }}
                  >
                    {"★".repeat(Math.round(parseFloat(post.ratings.overall))) +
                      "☆".repeat(5 - Math.round(parseFloat(post.ratings.overall)))}
                  </span>
                ) : (
                  <span style={{ marginLeft: "5px", fontSize: "14px", color: "#999" }}>
                    Not Rated
                  </span>
                )}
              </>
            )}
          </h3>

          {post.isQuestion && post.department && (
            <p><strong>Department:</strong> {post.department}</p>
          )}

          {expandedRatings[post.id] && post.ratings && (
            <div style={{ marginTop: "5px", fontSize: "14px", color: "#555" }}>
              {Object.entries(post.ratings).map(([key, value]) => {
                if (key === "overall") return null;
                const isValidRating = !isNaN(parseInt(value));
                const label = key.charAt(0).toUpperCase() + key.slice(1);
                return (
                  <div key={key}>
                    <strong>{label}:</strong>{" "}
                    {isValidRating
                      ? "★".repeat(parseInt(value)) + "☆".repeat(5 - parseInt(value))
                      : "N/A"}
                  </div>
                );
              })}
            </div>
          )}

          {!post.isQuestion && (
            <>
             <p>
  <strong>Posted by: </strong>
  <Link to={`/user/${post.userId}`} style={{ color: "blue", marginRight: "15px" }}>
    {post.username || "Anonymous"}
  </Link>
  <span style={{ marginRight: "20px" }}>
    <strong>Job Title:</strong> {post.position || "N/A"}
  </span>
  <span>
    <strong>Status:</strong> {post.employmentStatus || "N/A"}
  </span>
</p>


            </>
          )}

          <p style={{ whiteSpace: "normal", overflowWrap: "break-word"
, wordBreak: "break-word" }}>
            <strong>{post.isQuestion ? "Question" : "Post"}:</strong>{" "}
            {expandedPost === post.id ? post.comments : `${post.comments?.substring(0, 280)}...`}
           {post.comments && post.comments.length > 280 && (
  <button
    onClick={() => setExpandedPost(expandedPost === post.id ? null : post.id)}
    style={{
  border: "none",
  background: "none",
  color: "blue",
  cursor: "pointer",
  marginLeft: "5px",
  display: "inline",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%"
}}

  >
    {expandedPost === post.id ? "Show Less" : "Show More"}
  </button>
)}

          </p>


               {/* ✅ Like, Comment, and Follow Buttons with a Contemporary Look */}
<div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "5px", flexWrap: "wrap" }}>
  
  <p style={{ fontSize: "14px", color: "#333" }}>
  {post.createdAt?.toDate
    ? post.createdAt.toDate().toLocaleDateString()
    : "Unknown Date"}
</p>

  {/* ✅ Views Count */}
  <p style={{ margin: 0, fontSize: "14px", color: "#666" }}><strong>Views:</strong> {post.views || 0}</p>

  {/* ✅ Like Button - Minimalist In Left Column*/}
  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
    <button 
      className={`like-button ${likedPosts[post.id] ? "liked" : ""}`} 
      onClick={() => toggleLike(post.id)}
      style={{ 
        border: "none", 
        background: "none", 
        color: likedPosts[post.id] ? "red" : "#007bff", 
        fontSize: "14px", 
        cursor: "pointer"
      }}
    >
      ❤
    </button>
    <span style={{ fontSize: "14px", color: "#666" }}>{posts.find(p => p.id === post.id)?.likes || 0}</span> 
  </div>

  {/* ✅ Comment Button - Minimalist in Left Column */}
  {currentUser ? (
    <button 
  onClick={() => toggleReplyBox(post.id, "latest")} 
  style={{ border: "none", background: "none", color: "#007bff", fontSize: "14px", cursor: "pointer" }}
>
  {replyVisibility[`${post.id}-latest-main`] ? "Cancel" : "Comment"}
</button>

  ) : (
    <button 
  onClick={() => navigate("/login")} 
  style={{ 
    border: "none", 
    background: "none", 
    color: "#007bff", 
    fontSize: "14px", 
    cursor: "pointer" 
  }}
>
  Comment
</button>

  )}


  {/* ✅ Follow Button - Minimalist In Left Column */}
  {currentUser && post.userId !== currentUser.uid && (
    <button 
      onClick={() => handleFollowToggle(post.userId, post.username)}
      style={{ 
        border: "none", 
        background: "none", 
        color: "#007bff", 
        fontSize: "14px", 
        cursor: "pointer"
      }}
    >
      {followingUsers.some(f => f.userId === post.userId) ? "Unfollow" : "Follow"}
    </button>
  )}

</div>


{/* ✅ Comment Input Box In Left Column*/}
{replyVisibility[`${post.id}-latest-main`] && (
  <div style={{ marginTop: "10px", width: "100%", display: "block" }}>


    <textarea
      placeholder="Write a comment..."
      value={replyText[`${post.id}-latest-main`] || ""}
      onChange={(e) => setReplyText(prev => ({ ...prev, [`${post.id}-latest-main`]: e.target.value }))}
      style={{ width: "100%", marginTop: "5px" }}
    />
    <button
      onClick={() => handleSubmitReply(post.id, null, "latest")}

      className="modern-button"
      disabled={!replyText[`${post.id}-latest-main`]?.trim()}
      style={{ marginTop: "5px" }}
    >
      Send
    </button>
  </div>
)}

               
{/* ✅ Display Comments & Nested Replies in Trending Section - Left Column */}
{post.responses && post.responses.length > 0 && (
  <div style={{ marginTop: "10px", paddingLeft: "10px", borderLeft: "2px solid #ddd" }}>
    {(expandedComments[post.id] ? post.responses : post.responses.slice(0, 1)).map((response, index) => (
      <div key={index} style={{ marginBottom: "8px" }}>
        <p><strong>{response.username || "Anonymous"}:</strong> {response.text}</p>

        {/* ✅ Show Nested Replies Only If Expanded */}
        {expandedComments[post.id] && response.replies && response.replies.length > 0 && (
          <div style={{ marginLeft: "20px", paddingLeft: "10px", borderLeft: "1px solid #ccc" }}>
            {response.replies.map((reply, replyIndex) => (
              <p key={replyIndex} style={{ fontSize: "14px" }}>
                <strong>{reply.username || "Anonymous"}:</strong> {reply.text}
              </p>
            ))}
          </div>
        )}
    
	

        {/* ✅ Reply Button In Left Column */}
      <button 
  className="reply-button" 
  onClick={() => setReplyVisibility(prev => ({
    ...prev,
    [response.id]: prev[response.id] === "reply" ? null : "reply" // Toggle only this reply field
  }))}>
  Reply
</button>

 


        {/* ✅ Reply Input Box In Left Column */}
 {replyVisibility[response.id] === "reply" && (
  <div>
    <textarea
      placeholder="Write a reply..."
      value={replyText[response.id] || ""}
      onChange={(e) =>
        setReplyText(prev => ({ ...prev, [response.id]: e.target.value }))
      }
      style={{ width: "100%", marginTop: "5px" }}
    />
    {currentUser ? (
      <button
        onClick={() => handleSubmitReply(post.id, response.id)}
        className="modern-button"
        disabled={!replyText[response.id]?.trim()}
      >
        Send
      </button>
    ) : (
      <button
        onClick={() => navigate("/login")}
        className="modern-button"
        style={{ color: "#007bff" }}
      >
        Log in to send
      </button>



  		    )}
 		 </div>
	        )}
   	    </div>
   	    ))}



	{/* ✅ “See More Comments” / “Hide Comments” button*/}
	{(post.responses.length > 1 || (post.responses[0]?.replies?.length > 0)) && (
  <button
    onClick={() => toggleExpandedComments(post.id)}
    style={{
      marginTop: "5px",
      background: "none",
      border: "none",
      color: "#007bff",
      cursor: "pointer",
      fontSize: "14px"
    }}
  >
    {expandedComments[post.id]
      ? "Hide comments"
      : `See more comments (${post.responses.slice(1).reduce((acc, r) => acc + 1 + (r.replies?.length || 0), 0) + (post.responses[0]?.replies?.length || 0)})`}
  </button>
)}


 			      </div>
			    )}
        	      </li>
         	   ))}
                 </ul>
      	  	)}
     	     </div>
		)}

		
      {/* ✅ Right Column: Trending */}
      {(!isMobile || selectedView === "trending") && (
      <div
    style={{
      flex: "1.3",
      minWidth: "350px",
      maxWidth: "6100px",
      backgroundColor: "#d6efff",
      padding: "10px",
      borderRadius: "8px",
      boxShadow: "0 0 6px rgba(0,0,0,0.05)"
    }}
  >
    <h3 style={{
      textAlign: "center",
      fontSize: "24px",
      marginBottom: "10px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      justifyContent: "center"
    }}>
      📈 Trending
    </h3>
        {mostViewed.length === 0 ? (
  <p>No posts yet.</p>
) : (
  <div style={{ maxWidth: "980px", margin: "0 auto" }}>
    <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
      {mostViewed.slice(0, postLimit).map((post) => (
        <li
          key={post.id}
          style={{
            marginBottom: "10px",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            padding: "10px",
            backgroundColor: "#fff",
            position: "relative",
          }}
        >

                <strong>
                  {post.isQuestion ? (
                    <>
                      <strong>Question for: </strong>
                      {post.company} ({post.city}, {post.state})<br />
                      <strong>By: </strong>
                      {post.username || "Anonymous"}
                    </>
                  ) : (
                    <>
                      {post.company} ({post.city}, {post.state})
                      {post.ratings?.overall ? (
                        <span
                          onClick={() =>
        setExpandedRatings((prev) => ({
          ...prev,
          [`trending-${post.id}`]: !prev[`trending-${post.id}`],
        }))
      }
                          style={{ cursor: "pointer", color: "#f5c518", fontSize: "16px", marginLeft: "5px" }}
                        >
                          {"★".repeat(Math.round(post.ratings.overall)) +
                            "☆".repeat(5 - Math.round(post.ratings.overall))}
                        </span>
                      ) : (
                        <span style={{ marginLeft: "5px", fontSize: "14px", color: "#999" }}>Not Rated</span>
                      )}
                    </>
                  )}
                </strong>


		{expandedRatings[`trending-${post.id}`] && post.ratings && (
  <div style={{ marginTop: "5px", fontSize: "14px", color: "#555" }}>
    {Object.entries(post.ratings).map(([key, value]) => {
      if (key === "overall") return null;

      const isValidRating = !isNaN(parseInt(value));
      const label = key.charAt(0).toUpperCase() + key.slice(1);

      return (
        <div key={key}>
          <strong>{label}:</strong>{" "}
          {isValidRating
            ? "★".repeat(parseInt(value)) + "☆".repeat(5 - parseInt(value))
            : "N/A"}
        </div>
        );
    })}
	 </div>
)}	

                {post.isQuestion && (
                  <p><strong>Department:</strong> {post.department || "N/A"}</p>
                )}

                <p style={{ 
  wordBreak: "break-word", 
  whiteSpace: "normal", 
  overflowWrap: "break-word", 
  fontSize: "14px", 
  color: "#333" 
}}>
                  <strong>{post.isQuestion ? "Question:" : "Post:"}</strong> {expandedPost === post.id ? post.comments : `${post.comments?.substring(0, 280)}...`}
                  {post.comments && post.comments.length > 280 && (
  <button
    onClick={() => setExpandedPost(expandedPost === post.id ? null : post.id)}
    style={{ border: "none", background: "none", color: "blue", cursor: "pointer", marginLeft: "5px" }}
  >
    {expandedPost === post.id ? "Show Less" : "Show More"}
  </button>
)}

                </p>

                {!post.isQuestion && (
                  <p>
                    <strong>Posted by: </strong>
                    <Link to={`/user/${post.userId}`} style={{ color: "blue", marginRight: "10px" }}>
                      {post.username || "Anonymous"}
                    </Link>
                  </p>
	)}


      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <p style={{ margin: 0 }}>

		

        </p>
{/* T. Score + Buttons Block */}
<div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
  <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
    <p style={{ margin: 0 }}>
      <strong>T. Score:</strong> {Math.round(calculateTrendingScore(post))}
    </p>

    <button
      className={`like-button ${likedPosts[post.id] ? "liked" : ""}`}
      onClick={() => toggleLike(post.id)}
      style={{
        border: "none",
        background: "none",
        color: likedPosts[post.id] ? "red" : "#007bff",
        fontSize: "14px",
        cursor: "pointer",
      }}

    >
      ❤
    </button>
    <span style={{ fontSize: "14px", color: "#666" }}>
      {posts.find((p) => p.id === post.id)?.likes || 0}
    </span>

    {currentUser ? (
      <button
        onClick={() => toggleReplyBox(post.id, "trending")}
        style={{
          border: "none",
          background: "none",
          color: "#007bff",
          fontSize: "14px",
          cursor: "pointer",
        }}
      >
        {replyVisibility[`${post.id}-trending-main`] ? "Cancel" : "Comment"}
      </button>
    ) : (
      <p style={{ fontSize: "12px", color: "#007bff" }}>Log in to comment</p>
    )}

    {currentUser && post.userId !== currentUser.uid && (
      <button
        onClick={() => handleFollowToggle(post.userId, post.username)}
        style={{
          border: "none",
          background: "none",
          color: "#007bff",
          fontSize: "14px",
          cursor: "pointer",
        }}
      >
        {followingUsers.some((f) => f.userId === post.userId)
          ? "Unfollow"
          : "Follow"}
      </button>
    )}

	<p style={{ fontSize: "12px", color: "#333" }}>
  {post.createdAt?.toDate
    ? post.createdAt.toDate().toLocaleDateString()
    : "Unknown Date"}
</p>



  </div>

  {/* Comment Input Box Underneath */}
  {replyVisibility[`${post.id}-trending-main`] && (
    <div style={{ marginTop: "10px", width: "100%" }}>
      <textarea
        placeholder="Write a comment..."
        value={replyText[`${post.id}-trending-main`] || ""}
        onChange={(e) =>
          setReplyText((prev) => ({
            ...prev,
            [`${post.id}-trending-main`]: e.target.value,
          }))
        }
        style={{ width: "100%", marginTop: "5px" }}
      />
      <button
        onClick={() => handleSubmitReply(post.id, null, "trending")}

        className="modern-button"
        disabled={!replyText[`${post.id}-trending-main`]?.trim()}
        style={{ marginTop: "5px" }}
      >
        Send
      </button>
    </div>
  )}



               
{/* ✅ Display Comments & Nested Replies in Trending Section */}
{posts.find(p => p.id === post.id)?.responses?.length > 0 && (

  <div style={{ marginTop: "10px", paddingLeft: "15px", borderLeft: "2px solid #ddd" }}>
    {(expandedComments[post.id] 
  ? post.responses 
  : post.responses.slice(0, 1)
).map((response, index) => (

      <div key={index} style={{ marginBottom: "8px" }}>
        <p><strong>{response.username || "Anonymous"}:</strong> {response.text}</p>
      
   
        {/* ✅ Show Nested Replies */}
       {expandedComments[post.id] && response.replies && response.replies.length > 0 && (
  <div style={{ marginLeft: "20px", paddingLeft: "10px", borderLeft: "1px solid #ccc" }}>
    {response.replies.map((reply, replyIndex) => (
      <p key={replyIndex} style={{ fontSize: "14px" }}>
        <strong>{reply.username || "Anonymous"}:</strong> {reply.text}
      </p>
    ))}
  </div>
)}
    


        {/* ✅ Reply Button */}
      <button 
  className="reply-button" 
  onClick={() => setReplyVisibility(prev => ({
    ...prev,
    [response.id]: prev[response.id] === "reply" ? null : "reply" // Toggle only this reply field
  }))}>
  Reply
</button>


        {/* ✅ Reply Input Box */}
  {replyVisibility[response.id] === "reply" && (
  <div>
    <textarea
      placeholder="Write a reply..."
      value={replyText[response.id] || ""}
      onChange={(e) =>
        setReplyText(prev => ({ ...prev, [response.id]: e.target.value }))
      }
      style={{ width: "100%", marginTop: "5px" }}
    />
    {currentUser ? (
      <button
        onClick={() => handleSubmitReply(post.id, response.id)}
        className="modern-button"
        disabled={!replyText[response.id]?.trim()}
      >
        Send
      </button>
    ) : (
      <button
        onClick={() => navigate("/login")}
        className="modern-button"
        style={{ color: "#007bff" }}
      >
        Log in to send
      </button>
   				 )}
 			     </div>
			    )}

		      </div>
    		     ))}

	{/* ✅ “See More Comments” / “Hide Comments” button*/}
	{(post.responses.length > 1 || (post.responses[0]?.replies?.length > 0)) && (
  <button
    onClick={() => toggleExpandedComments(post.id)}
    style={{
      marginTop: "5px",
      background: "none",
      border: "none",
      color: "#007bff",
      cursor: "pointer",
      fontSize: "14px"
    }}
  >
    {expandedComments[post.id]
      ? "Hide comments"
      : `See more comments (${post.responses.slice(1).reduce((acc, r) => acc + 1 + (r.replies?.length || 0), 0) + (post.responses[0]?.replies?.length || 0)})`}
  </button>
						)}
			 		    </div>
					    )}
       	   			          )}
      				       </div>
     				    </div>	
  				  </li>
 				 ))}
	       		      </ul>
				</div>
    			     )}
     			 </div>
   			 )} 
  		      </div>	
 		    </div>  
		    );
		   }


export default HomePage;
